import { Telegram as TelegramIcon } from '@mui/icons-material';
import { Tab, Button } from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PageHeader,
  QueryTabPanel,
  QueryTabs,
  TelegramSupportDialog,
} from 'components';
import { OrdersTab } from 'enums';
import { useWhiteTriangle, useMeta, useQueryTab } from 'hooks';
import { TranslationNamespace } from 'i18n';

import { TraderActiveOrders } from './ActiveOrders';
import { TraderAllOrders } from './AllOrders';
import { TraderCancelledOrders } from './CancelledOrders';
import { TraderCompletedOrders } from './CompletedOrders';
import { TraderDisputeOrders } from './DisputeOrders';
import { TraderDisputeOrdersArchive } from './DisputeOrdersArchive';
import { TraderHoldCompletedOrders } from './HoldCompletedOrders';
import { TraderRequisitesVerificationOrders } from './RequisitesVerificationOrders';

export const TraderPayinOrdersPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { tab } = useQueryTab();
  const { isWhiteTriangle } = useWhiteTriangle();

  const [telegramSupportDialogOpen, setTelegramSupportDialogOpen] =
    useState(false);

  const { telegramSupportBotUrl } = useMeta();
  const title = useMemo(
    () => (isWhiteTriangle ? t('white_triangle_title') : t('title')),
    [t, isWhiteTriangle],
  );

  return (
    <Fragment>
      <div className="tw-w-full">
        <PageHeader
          title={title}
          rightContent={
            tab === OrdersTab.Dispute &&
            telegramSupportBotUrl && (
              <Button
                color="primary"
                variant="outlined"
                startIcon={<TelegramIcon />}
                onClick={() => setTelegramSupportDialogOpen(true)}
              >
                {tCommon('buttons.telegram_support')}
              </Button>
            )
          }
          divider={false}
        />
      </div>
      <QueryTabs tabsEnum={OrdersTab}>
        <Tab value={OrdersTab.Active} label={t('tabs.active')}></Tab>
        <Tab value={OrdersTab.Completed} label={t('tabs.completed')} />
        <Tab value={OrdersTab.HoldCompleted} label={t('tabs.hold_completed')} />
        <Tab value={OrdersTab.Cancelled} label={t('tabs.cancelled')} />
        <Tab value={OrdersTab.Dispute} label={t('tabs.dispute')} />
        <Tab
          value={OrdersTab.DisputeArchive}
          label={t('tabs.dispute_archive')}
        />
        <Tab
          value={OrdersTab.RequisitesVerification}
          label={t('tabs.requisites_verification')}
        />
        <Tab value={OrdersTab.All} label={t('tabs.all')} />
      </QueryTabs>
      <QueryTabPanel value={OrdersTab.Active}>
        <TraderActiveOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Completed}>
        <TraderCompletedOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.HoldCompleted}>
        <TraderHoldCompletedOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Cancelled}>
        <TraderCancelledOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Dispute}>
        <TraderDisputeOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.DisputeArchive}>
        <TraderDisputeOrdersArchive />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.RequisitesVerification}>
        <TraderRequisitesVerificationOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.All}>
        <TraderAllOrders />
      </QueryTabPanel>
      {tab === OrdersTab.Dispute && (
        <TelegramSupportDialog
          open={telegramSupportDialogOpen}
          onClose={() => setTelegramSupportDialogOpen(false)}
        />
      )}
    </Fragment>
  );
};
